import { mapDesigns } from "@mono/mapDesigns"
import { socket } from "@/utils/utils"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import PropertyBidCard from "@/Components/PropertyBigCard"
import Money from "@/Components/Money"
import ModalButton from "@/Components/ModalButton"
import GameTimer from "@/Components/GameTimer"
import Modal from "@/Components/Modal"
import { useTimerTickSound } from "@/Hooks/useTimerTickSound"
import { useGameTimings } from "@/Hooks/useGameTimings"

const DoubleRentModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  // Only for `headerSize`, which is a prop rather than a class and so cannot be
  // answered with a `mobile:` step like everything else below.
  const isMobile = useAppSelector((state) => state.global.isMobile)
  const { cardDecisionTime } = useGameTimings()
  const modalData = useAppSelector((state) => state.global.modalData)
  const myMapId = myGame?.settings?.mapId
  const myMap = mapDesigns.find((map) => map.id === myMapId)
  const activePropertyId = modalData.doubleRent?.propertyId
  const property = myMap?.cells.find((cell) => cell.order === activePropertyId)
  const rent = modalData.doubleRent?.rent || 0
  const doubledRent = rent * 2
  useTimerTickSound(
    !!modalData.doubleRent?.visible && !!property,
    "cardDecisionTimeLeft"
  )

  const handleDismiss = () => {
    const doubleRentModal = modalData.doubleRent
    socket.emit("game.doubleRentResponse", {
      ownerOrder: doubleRentModal!.ownerOrder || 0,
      playerOrder: doubleRentModal?.playerOrder || 0,
      propertyOrder: doubleRentModal?.propertyId || 0,
      roomid: myGame!.id,
      forced: false,
    })
    dispatch(setModalData({ doubleRent: { visible: false } }))
  }

  const handleDoubleRent = () => {
    socket.emit("game.playActionCard", {
      roomid: myGame!.id,
      cardId: "dobule-rent",

      playerOrder: modalData.doubleRent?.ownerOrder as PlayerOrder,
      data: {
        targetedPlayerOrder: modalData.doubleRent?.playerOrder as PlayerOrder,
      },
    })
    const doubleRentModal = modalData.doubleRent
    socket.emit("game.doubleRentResponse", {
      ownerOrder: doubleRentModal!.ownerOrder || 0,
      playerOrder: doubleRentModal?.playerOrder || 0,
      propertyOrder: doubleRentModal?.propertyId || 0,
      roomid: myGame!.id,
      forced: true,
    })
    dispatch(setModalData({ doubleRent: { visible: false } }))
  }

  if (!modalData.doubleRent?.visible || !property) return null

  return (
    <Modal
      data-testid="game.double-rent-modal"
      // Centered on the board it is asking about, which is the middle a player
      // actually sees. Horizontally that is the middle of the play box: on a
      // phone the game is inset from the screen edges (App.css, --play-w) and a
      // notch makes those insets uneven — in landscape the camera edge is worth
      // ~34px against the opposite edge's 8px — so a viewport-centered panel
      // would sit ~13px off the board's middle. Vertically the board runs the
      // full height of the screen (Board.tsx), so its middle is plain 50vh.
      // Both come to the same thing on a desktop, where the insets are 0.
      //
      // The panel hugs its content instead of taking a share of the width. On a
      // phone `w-[90%]` was ~760px of box around ~300px of content, so the modal
      // read as the whole screen with a column of small text in a corner.
      className="fixed top-[50vh] left-[calc(var(--safe-left)_+_var(--play-w)/2)] -translate-x-1/2 -translate-y-1/2 z-10 w-auto max-w-[calc(var(--play-w)_-_16px)]"
      // Below 1000px (`mobile:`) index.css halves the root font size, so every
      // rem-based step lands at half of what it reads as on a desktop. The
      // repo's answer is to restate the phone sizes in raw px (FastPaceVotePanel,
      // PropertyBigCardModal), and the header is part of that: its title and
      // subtitle would otherwise land at 12px and 7px.
      icon={
        <i className="fa-money-bill-wave fa-solid text-white text-2xl mobile:text-[11px]"></i>
      }
      title={<span className="mobile:text-[15px]">Double Rent</span>}
      subtitle={
        <span className="mobile:text-[10px]">
          Charge double rent, or let it go
        </span>
      }
      // The header is the cheapest height on a phone: the badge and its paddings
      // are decoration around three words. `sm` takes the badge from 28px to
      // 20px and the paddings with it, ~30px off the panel, and the title and
      // subtitle keep their px sizes from above regardless.
      headerSize={isMobile ? "sm" : "default"}
      headerClassName="mobile:px-[12px]"
      contentClassName="flex gap-4 px-4 md:px-8 pb-4 md:pb-8 mobile:px-[12px] mobile:pb-[10px]"
    >
      {/* The card is what makes this modal wide, and a phone has no size for it
          that works: at the halved rem scale a `wide` card is a 160px thumbnail
          with a 7px rent table, and `mobile-big` — the readable one — is about
          230x265, which cannot clear a landscape phone's height under the
          header. The decision only needs the property's name and the two
          amounts, and those are in the column, so the card sits this one out. */}
      <div className="shrink-0 mobile:hidden">
        <PropertyBidCard cell={property} size="wide"></PropertyBidCard>
      </div>
      <div className="flex flex-col justify-between w-[15rem] shrink-0 text-white mobile:w-[220px] mobile:gap-[7px]">
        {/* Timer */}
        <div className="w-full flex justify-center mb-4 mobile:mb-0">
          <GameTimer
            timerKey="cardDecisionTimeLeft"
            totalTime={cardDecisionTime}
            size="md"
            showLabel={true}
            labelText="Decision Time"
            // The ring is rem-sized like the rest of StaticTimer, so on a phone
            // it comes out at 28px — proportionate — but the seconds inside it
            // drop to 10px, and that number is the one thing here that is read
            // at a glance.
            timeClassName="mobile:text-[13px]"
          />
        </div>

        <div className="flex flex-col gap-2 md:gap-4 items-center mobile:gap-[5px]">
          <div
            className="font-poppins text-2xl md:text-4xl text-center font-semibold mobile:text-[16px] mobile:leading-tight"
            data-testid="game.double-rent.name"
          >
            {property.propertyDetails?.name}
          </div>
          {/* One line on a phone — a label stacked over its own number is a row
              of height for a sentence that fits across the column anyway. */}
          <div className="flex flex-col items-center gap-2 mobile:flex-row mobile:gap-[5px]">
            <div className="text-white font-poppins text-base md:text-lg mobile:text-[11px]">
              Rent to Pay:
            </div>
            <Money
              money={rent}
              className="gap-2 font-poppins text-xl md:text-2xl mobile:text-[15px]"
              iconClass="h-2/3"
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 md:gap-4 mt-4 mobile:gap-[8px] mobile:mt-0">
          <ModalButton
            color="green"
            onClick={handleDoubleRent}
            data-testid="game.double-rent.double-btn"
            className="w-full min-w-[13rem] whitespace-nowrap mobile:min-w-0 mobile:px-[10px] mobile:py-[6px] mobile:rounded-[12px]"
          >
            <div className="flex flex-col items-center gap-0.5 mobile:gap-[1px]">
              <div className="flex items-center justify-center gap-2 mobile:gap-[6px]">
                <i className="fa-money-bill-wave fa-solid text-white fa-lg md:fa-xl"></i>
                <span className="text-white font-medium font-poppins text-lg md:text-xl mobile:text-[13px]">
                  Double Rent
                </span>
                <Money
                  money={doubledRent}
                  className="gap-1 font-poppins text-lg md:text-xl font-bold text-white mobile:text-[13px]"
                  iconClass="h-2/3"
                />
              </div>
              <span className="text-white/80 font-poppins font-normal text-xs mobile:text-[9px]">
                Uses your card
              </span>
            </div>
          </ModalButton>
          <ModalButton
            color="red"
            onClick={handleDismiss}
            data-testid="game.double-rent.dismiss-btn"
            className="w-full min-w-[13rem] whitespace-nowrap mobile:min-w-0 mobile:px-[10px] mobile:py-[6px] mobile:rounded-[12px]"
          >
            <div className="flex flex-col items-center gap-0.5 mobile:gap-[1px]">
              <div className="flex items-center justify-center gap-2 mobile:gap-[6px]">
                <i className="fa-hand-holding fa-solid text-white fa-lg md:fa-xl"></i>
                <span className="text-white font-medium font-poppins text-lg md:text-xl mobile:text-[13px]">
                  Dismiss
                </span>
              </div>
              <span className="text-white/80 font-poppins font-normal text-xs mobile:text-[9px]">
                Charge normal rent
              </span>
            </div>
          </ModalButton>
        </div>
      </div>
    </Modal>
  )
}

export default DoubleRentModal
